import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import InstallAppButton from './InstallAppButton';
import { 
  Coffee, 
  LayoutDashboard, 
  Package, 
  Flame, 
  ShoppingCart, 
  Users, 
  AlertTriangle,
  DollarSign,
  Shield,
  Box,
  FileText,
  User,
  LogOut,
  Menu,
  X,
  Trash2
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Green Inventory', href: '/inventory', icon: Package },
    { name: 'Roasting', href: '/roasting', icon: Flame },
    { name: 'Roasted Stock', href: '/roasted-stock', icon: Coffee },
    { name: 'Expenses', href: '/expenses', icon: DollarSign },
    { name: 'Wastage', href: '/wastage', icon: AlertTriangle },
    ...(user?.role === 'Admin' ? [
      { name: 'Users', href: '/users', icon: Shield },
      { name: 'Activity Logs', href: '/activity-logs', icon: FileText },
      { name: 'Recently Deleted', href: '/recently-deleted', icon: Trash2 }
    ] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen w-64 bg-gradient-to-b from-amber-600 to-orange-700 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:relative lg:z-auto
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-amber-500/30">
            <div className="flex items-center space-x-3">
              <div className="bg-white p-2 rounded-lg shadow-lg">
                <Coffee className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Evabrew Roastery Management</h1>
                <p className="text-xs text-amber-100">Operations Dashboard</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-all
                    ${isActive 
                      ? 'bg-white text-amber-600 shadow-lg font-semibold' 
                      : 'text-white hover:bg-amber-500/30'
                    }
                  `}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-amber-500/30">
            <div className="bg-amber-500/20 rounded-lg p-4">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-amber-100">{user?.role}</p>
              <p className="text-xs text-amber-200 mt-1 truncate">{user?.email}</p>
              <div className="mt-3 space-y-2">
                <InstallAppButton />
                <Link
                  to="/profile"
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-amber-700 text-white rounded-lg text-sm font-medium hover:bg-amber-800 transition"
                >
                  <User className="w-4 h-4" />
                  <span>My Profile</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-white text-amber-600 rounded-lg text-sm font-medium hover:bg-amber-50 transition"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition flex-shrink-0"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          <div className="flex items-center space-x-2 min-w-0">
            <Coffee className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="font-semibold text-gray-800 text-sm truncate">Evabrew</span>
          </div>
          <InstallAppButton
            label={false}
            className="p-2 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition flex-shrink-0"
          />
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
